<template>
	<UPage>
		<UPageBody>
			<UContainer>
				<h2 class="text-4xl sm:text-5xl flex pb-8 sm:pb-10 font-semibold flex-col tracking-tight text-center">
					<span class="text-primary">
						{{ t('blog-main.h2.title') }}
					</span>
					<span class="text-default">
						{{ t('blog-main.h2.title-2') }}
					</span>
				</h2>
				<UBlogPosts>
					<UBlogPost
						v-for="(post, index) in posts"
						v-bind="post"
						:id="`${BLOG_CARD_ID}_${index}`"
						:key="index"
						:to="post.to"
						:image="{
							src: `${post.image}/m/324x162`,
							srcset: `${post.image}/m/220x110 324w, ${post.image}/m/250x125 500w, ${post.image}/m/320x160 800w, ${post.image}/m/420x210 1000w`,
							loading: 'lazy'
						}"
						:orientation="index === 0 ? 'horizontal' : 'vertical'"
						:ui="{
							root: 'first-of-type:col-span-1 sm:first-of-type:col-span-2 lg:first-of-type:col-span-3',
							date: 'order-last'
						}"
						@click="trackEvent($event, BLOG_PAGE_LOCATIONS.list, {
							title: post.title
						})"
						@mouseenter="trackEvent($event, BLOG_PAGE_LOCATIONS.list, {
							title: post.title
						})"
						@focus="trackEvent($event, BLOG_PAGE_LOCATIONS.list, {
							title: post.title
						})"
					>
						<template #date>
							<div class="flex flex-col gap-2">
								<div
									v-if="index === 0"
									class="hidden text-sm text-primary sm:block group-hover/blog-post:underline underline-offset-2 decoration-2 decoration-primary transition-all duration-150 ease-in-out"
								>
									{{ t('blog-article.read-more') }}
								</div>
								<div class="text-muted mt-auto">
									<NuxtTime
										:datetime="new Date(post.date || '')"
										month="short"
										day="numeric"
										year="numeric"
									/>
								</div>
							</div>
						</template>
					</UBlogPost>
				</UBlogPosts>
			</UContainer>
		</UPageBody>
	</UPage>
</template>

<script setup lang="ts">
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'
import { BLOG_CARD_ID } from '@/constants/ga4/tracking-ids'
import { BLOG_PAGE_LOCATIONS } from '@/constants/ga4/tracking-locations'
import { BASE_URL } from '@/constants/index.js'
import { OG_IMAGE_DEFAULT } from '@/constants/og-image'
import type { BlogPostProps } from '@nuxt/ui-pro'
import { useI18n } from 'vue-i18n'

const { bugsnagReport } = useErrorReporter()
const { trackEvent } = useGA4Tracking()

const {
	t,
	locale
} = useI18n({
	useScope: 'global'
})

useHead({
	title: t('blog-main.use-head.title'),
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})

defineProps({
	page: {
		type: Number,
		required: true
	}
})

const blogStories = useState<any[]>(`blog-stories-${locale.value}`, () => [])

// If no blogStories found in SSG payload,
// then fetch via server API during SSR to avoid client-side Storyblok calls
if (!blogStories.value?.length) {
	const {
		data, error
	} = await useFetch('/api/blog-articles', {
		params: { language: locale.value }
	})
	if (error.value) {
		bugsnagReport({
			error: new Error('API Error - No blog articles found'),
			showToast: true
		})
	}
	// set blogStories in payload state
	blogStories.value = data.value || []
	if (!blogStories.value || !blogStories.value?.length) {
		// If no articles are found, report an error
		bugsnagReport({
			error: new Error('No blog articles found in Storyblok'),
			showToast: true
		})
	}
}

const posts = computed<BlogPostProps[]>(() => {
	return blogStories.value?.map((story: any): BlogPostProps => {
		const {
			image, title, dateTime, cardBody: preview
		} = story.content
		return {
			title: title || '',
			description: preview || '',
			date: dateTime || '',
			image: image.filename || '',
			to: `/${story.full_slug}`
		}
	})
})

const { getStory } = useStoryblokWithErrorReporting()

const storyblok = await getStory('blog')

const { story } = storyblok.value?.data || {}

if (!story) {
	bugsnagReport({
		error: new Error('No blog story found'),
		showToast: true
	})
}

const {
	title = '',
	description = ''
} = story.content.metatags

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})
 import.meta.prerender && defineOgImage('Default', OG_IMAGE_DEFAULT)
</script>
